import React, { useContext } from 'react'
import PropTypes from 'prop-types'
import swal from 'sweetalert2'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faDownload, faRepeat, faTrashAlt } from '@fortawesome/pro-light-svg-icons'

import {
  Box, Button, FileInput, Form, ModalContext, SectionDivider, SidebarModal, SidebarModalListItem,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import { useSetState } from '@domain-group/react-hooks'

import useCurrentUser from '@hooks/useCurrentUser'
import useFont, { useFontForm } from '@hooks/useFont'

import defaultRequestOptions from '@sections/Admin/packs/SystemManager/defaultRequestOptions'

const confirmDelete = (props, deleteFn) => {
  const { callbacks: { closeModal, deleteFont } } = props

  const deleteFontPayload = {
    callbacks: {
      action: deleteFn,
      afterAction: closeModal,
    },
    toastText: 'Font Deleted',
  }

  swal
    .fire({
      title: 'Delete Font Weight?',
      text: 'Are you sure?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it.',
      confirmButtonColor: '#DD6B55',
      animation: 'slide-from-top',
    })
    .then(({ value }) => {
      if (value){
        deleteFont(deleteFontPayload)
      }
    })
}

const createOrUpdateFont = (entityState, props, createFn, updateFn) => {
  const {
    callbacks: { closeModal, createFont, updateFont },
  } = props

  const text = entityState.id ? 'Updated' : 'Created'
  const actionFn = entityState.id ? updateFn : createFn

  const fontRequestOptions = {
    ...defaultRequestOptions.font,
    ...defaultRequestOptions.fontFamily,
    include_font_family: true,
  }

  const fontPayload = {
    callbacks: {
      action: actionFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    requestOptions: fontRequestOptions,
    toastText: `Font ${text}`,
  }

  if (entityState.id) return updateFont(fontPayload)

  return createFont(fontPayload)
}

const defaultState = {
  showFileUpload: false,
}

const CreateOrEditFontModal = (props) => {
  const { callbacks, modalKey = 'CreateOrEditFontModal', showModal } = props

  const [state, setState] = useSetState(defaultState)
  const { showFileUpload } = state

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, modalKey, {})
  const { font } = modalPayload

  const { isMasterAdmin } = useCurrentUser()

  const fontPayload = useFont(font)

  const {
    callbacks: {
      createFont: createFn,
      deleteFont: deleteFn,
      updateFont: updateFn,
    },
    creating,
    deleting,
    fontFileUrl,
    fontFileName,
    hasFile,
    updating,
  } = fontPayload

  const formPayload = useFontForm(font, { validateOn: 'change' })

  const {
    entityState,
    entityState: {
      file_data,
      id,
      match_title,
      title,
    },
    handlers,
    saveEnabled,
    setEntityState,
  } = formPayload

  const isEditing = !!id
  const saveOrCreateButtonText = isEditing ? 'Save Changes' : 'Add Font'

  const formSaveEnabled = saveEnabled && (!!file_data || hasFile)

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={modalKey} showModal={showModal} size="small">
      <SidebarModal.Header callbacks={callbacks} title={id ? 'Edit' : 'Add'} titleSecondLine="Font" />

      <SidebarModal.Content>
        <Box flexDirection="column">
          <Form.Field label="* Title">
            <input name="title" type="text" value={title} {...handlers} />
          </Form.Field>

          <Form.Field label="* Import Match" marginTop="large">
            <input name="match_title" type="text" value={match_title} {...handlers} />
          </Form.Field>

          {(!isEditing || showFileUpload) && (
            <Form.Field label="File" marginTop="large">
              <FileInput
                input={{
                  onChange: (fileData, fileObject) => (
                    setEntityState({ file_data: fileData, file_name: fileObject.name })
                  ),
                }}
              />
            </Form.Field>
          )}

          {isEditing && (
            <>
              {hasFile && (
                <>
                  <SectionDivider boxProps={{ marginTop: 'large' }}>FILE</SectionDivider>

                  <Box>
                    <SidebarModalListItem title={fontFileName}>
                      <SidebarModalListItem.Actions>
                        <SidebarModalListItem.Icon href={fontFileUrl} icon={faDownload} target="_blank" />
                      </SidebarModalListItem.Actions>
                    </SidebarModalListItem>
                  </Box>
                </>
              )}

              <Form.Field label="More Options" marginTop={hasFile ? 0 : 'large'}>
                {!showFileUpload && (
                  <Button
                    buttonStyle="secondaryUtility"
                    icon={<FontAwesomeIcon icon={faRepeat} />}
                    onClick={() => setState({ showFileUpload: true })}
                    size="medium"
                  >
                    Replace File
                  </Button>
                )}

                {isMasterAdmin && (
                  <Button
                    buttonStyle="secondaryUtility"
                    icon={<FontAwesomeIcon icon={faTrashAlt} />}
                    loading={deleting}
                    marginTop={showFileUpload ? 0 : 'medium'}
                    onClick={() => confirmDelete(props, deleteFn)}
                    size="medium"
                  >
                    Delete Font
                  </Button>
                )}
              </Form.Field>
            </>
          )}

        </Box>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!formSaveEnabled}
          loading={creating || updating}
          onClick={() => createOrUpdateFont(entityState, props, createFn, updateFn)}
          size="large"
        >
          {formSaveEnabled ? saveOrCreateButtonText : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

CreateOrEditFontModal.propTypes = {
  callbacks: PropTypes.object.isRequired,
  modalKey: PropTypes.string,
  showModal: PropTypes.bool,
}

const LazyLoadedModal = props => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditFontModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
